import { Box, Divider, Typography } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import type { TrackingStats } from '../../../services/tracking.service';
import type { DashboardPanel } from '../types';
import { metricLabel, formatMetric } from '../format';

const LOWER_BETTER = new Set(['cost', 'cpl', 'cpv']);

function Delta({ metric, cur, prev }: { metric: string; cur: unknown; prev: unknown }) {
  const c = Number(cur); const p = Number(prev);
  if (!Number.isFinite(c) || !Number.isFinite(p)) return null;
  const pct = p === 0 ? (c > 0 ? 100 : 0) : ((c - p) / Math.abs(p)) * 100;
  if (Math.abs(pct) < 0.05) {
    return <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>0%</Typography>;
  }
  const up = pct > 0;
  const good = LOWER_BETTER.has(metric) ? !up : up;
  const Icon = up ? ArrowUpward : ArrowDownward;
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{ color: good ? 'success.main' : 'error.main', display: 'inline-flex', alignItems: 'center', fontWeight: 700 }}
    >
      <Icon sx={{ fontSize: 13 }} />{Math.abs(pct).toFixed(1)}%
    </Typography>
  );
}

export default function StatPanel({ panel, stats }: { panel: DashboardPanel; stats: TrackingStats }) {
  const totals = stats.totals as Record<string, unknown> | undefined;
  const prev = stats.previous_totals as Record<string, unknown> | undefined;
  const showDelta = !!panel.query.compare && !!prev;
  // With a single metric the panel title already names it — a per-metric label would just repeat it.
  const showLabel = panel.query.metrics.length > 1;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', px: 1 }}>
      {panel.query.metrics.map((m, i) => (
        <Box key={m} sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          {i > 0 && <Divider orientation="vertical" flexItem sx={{ my: 1 }} />}
          <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0, px: 0.5 }}>
            {showLabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                display="block"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, fontSize: '0.68rem', mb: 0.25 }}
              >
                {metricLabel(m)}
              </Typography>
            )}
            <Typography noWrap sx={{ fontSize: { xs: 20, md: 25 }, fontWeight: 700, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>
              {formatMetric(m, totals?.[m])}
            </Typography>
            {showDelta && (
              <Box sx={{ mt: 0.25, display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                <Delta metric={m} cur={totals?.[m]} prev={prev?.[m]} />
                <Typography component="span" variant="caption" color="text.secondary">vs prev</Typography>
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
